/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createAction } from '@reduxjs/toolkit';

import { HorseSelectionData } from '~/components/HorseSelection';
import { GameMode } from '~/game';
import { GameType, GameVisibility } from '~/server/src/dtos';
import type { ChatVisibility } from '~/server/src/dtos/game';

import { TournamentOverviewParsed } from './tournament/utils';

export interface CreateGameParameters {
  gameName: string;
  playerCount: number;
  horseCount: number;
  horseSelection: HorseSelectionData;
  visibility?: GameVisibility;
  allowSpectators?: boolean;
  chatVisibility?: ChatVisibility;
}

export interface JoinGameParameters {
  gameId: string;
  gameType: GameType;
  horseSelection: HorseSelectionData;
}

export interface JoinTournamentParameters {
  tournament: TournamentOverviewParsed;
  gameType: GameType;
  horseSelection: HorseSelectionData;
}
export interface RestoreGameParameters {
  mode: GameMode;
  userId?: string;
  preferredGameId?: string;
}

export const isRestoreGameParameters = (params: unknown): params is RestoreGameParameters => {
  return typeof (params as RestoreGameParameters).mode === 'string';
};

export const createFree = createAction<CreateGameParameters>('createFree');
export const joinFree = createAction<JoinGameParameters>('joinFree');
/** Dispatched when leaving the free setup page so the polling saga is cancelled. */
export const cancelSetupFree = createAction('cancelSetupFree');
/** Dispatched on-demand from the free setup screen to refresh the game list. */
export const refreshFreeGames = createAction('refreshFreeGames');
export const restoreGame = createAction<RestoreGameParameters>('restoreGame');
export const rejoinGame = createAction('rejoinGame');
export const joinTournament = createAction<JoinTournamentParameters>('joinTournament');
/** Dispatched on-demand from the tournament setup screen to refresh the tournament list. */
export const refreshTournaments = createAction('refreshTournaments');

/** Dispatched when join/create fails (e.g. email not verified). Payload is server error code. */
export const joinFreeFailed = createAction<{ error: string }>('setup/joinFreeFailed');
export const joinTournamentFailed = createAction<{ error: string }>('setup/joinTournamentFailed');
export const createFreeFailed = createAction<{ error: string }>('setup/createFreeFailed');
